import { Action, Reducer } from 'redux';
import * as microsoftTeams from '@microsoft/teams-js';
import { GetLocalStorage, SetLocalStorage } from './Misc';

// -----------------
// STATE - This defines the type of data maintained in the Redux store.

export interface TeamsState {
    InTeams: boolean | null;
    currentTeamsConfig: microsoftTeams.app.Context | null,
    themeString: string,
}

// -----------------
// ACTIONS - These are serializable (hence replayable) descriptions of state transitions.
// They do not themselves have any side-effects; they just describe something that is going to happen.
// Use @typeName and isActionType for type detection that works even after serialization/deserialization.

export interface SetTeamsConfigAction {
    type: 'SET_TEAMS_CONFIG',
    teamsConfig: microsoftTeams.app.Context | null,
}
export interface SetNotInTeamsAction {
    type: 'SET_NOT_IN_TEAMS',
}
export interface SetThemeString {
    type: 'SET_THEMESTRING',
    themeString: string,
}

// Declare a 'discriminated union' type. This guarantees that all references to 'type' properties contain one of the
// declared type strings (and not any other arbitrary string).
export type KnownAction = SetTeamsConfigAction | SetNotInTeamsAction | SetThemeString;

// ----------------
// ACTION CREATORS - These are functions exposed to UI components that will trigger a state transition.
// They don't directly mutate state, but they can have external side-effects (such as loading data).

export const TeamsSateActionCreators = {
    setTeamsConfig: (teamsConfig: microsoftTeams.app.Context) => ({ type: 'SET_TEAMS_CONFIG', teamsConfig: teamsConfig } as SetTeamsConfigAction),
    setNotInTeams: () => ({ type: 'SET_NOT_IN_TEAMS' } as SetNotInTeamsAction),
    setThemeString: (themeString: string) => ({ type: 'SET_THEMESTRING', themeString: themeString } as SetThemeString),
};

// ----------------
// REDUCER - For a given state and action, returns the new state. To support time travel, this must not mutate the old state.

export const reducer: Reducer<TeamsState> = (state: TeamsState | undefined, incomingAction: Action): TeamsState => {
    if (state === undefined) {
        return { InTeams: null, currentTeamsConfig: null, themeString: GetLocalStorage('DefaultTheme')??'webLightTheme' };
    }

    const action = incomingAction as KnownAction;
    switch (action.type) {
        case 'SET_TEAMS_CONFIG':
            return {
                ...state,
                InTeams: true,
                currentTeamsConfig: action.teamsConfig
            };
        case 'SET_NOT_IN_TEAMS':
            return {
                ...state,
                InTeams: false
            };
        case 'SET_THEMESTRING':
            SetLocalStorage('DefaultTheme', action.themeString);
            return {
                ...state,
                themeString: action.themeString
            };
        default:
            return state;
    }
};